import Auth, { AuthClass } from '@aws-amplify/auth';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { FunctionComponent, memo, useEffect, useState } from 'react';
import { useConfig } from '../../eliot-components/src/ConfigProvider/hooks';
import { AuthContext } from './hooks';
import {
  AuthUser,
  hasOneOfGlobalRoles,
  hasRoleInAnyUsersOrg,
  Role,
} from './types';
import { useManageContext } from '../ManageContext/ManageContext';
import Header from '../../Layout/components/Header/Header';
import { MediaDown, MediaUp } from '../../components/MediaQueries/MediaQueries';
import Visible from '../../components/Visible/Visible';
import MainDrawer from '../../Layout/components/MainDrawer/MainDrawer';
import { makeStyles, Theme } from '@material-ui/core';
import SelectOrgDialog from '../../Layout/components/Header/components/SelectOrgDialog/SelectOrgDialog';
import { useGetUserQuery } from '../../store/api-slice';

interface Props {
  readonly auth?: AuthClass;
  readonly isLoadingVisible?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexGrow: 1,
    paddingTop: theme.mixins.toolbar.minHeight,
  },
}));

const initialUser: AuthUser = {
  id: '',
  email: '',
  orgs: [],
  selectedOrgs: [],
};

const AuthProvider: FunctionComponent<Props> = memo(
  ({ auth = Auth, isLoadingVisible = true, ...props }) => {
    const classes = useStyles();
    const config = useConfig();
    const { state, dispatch } = useManageContext();
    const resultsVisible =
      state.isAppSearchVisible &&
      ((state.searchQuery && state.searchQuery.length > 0) ||
        (state.advancedSearchQuery && state.advancedSearchQuery.length > 0) ||
        (state.advancedSearchFilters &&
          Object.keys(state.advancedSearchFilters).length > 0));
    let [sess, setSess] = useState<CognitoUserSession | null>(null);

    const token = sess ? sess.getIdToken().decodePayload() : null;
    const { data: usr, isFetching: isLoading } = useGetUserQuery(
      {
        apiBaseURL: config.apiBaseURL,
        id: token?.sub,
        token,
      },
      { skip: !token }
    );

    const selectOrgs = (orgs: string[]) => {
      localStorage.setItem('user.selected_org', orgs.join(';'));
      dispatch({
        type: 'selected-org-changed',
        data: orgs.map((o) => ({ id: o })),
      });
    };

    useEffect(() => {
      auth
        .currentSession()
        .then((s) => {
          setSess(s);
        })
        .catch(() => {
          auth.signOut();
          localStorage.clear();
          window.location.replace('/login');
        });
    }, [auth]);

    useEffect(() => {
      if (usr) {
        const hasPerm =
          hasOneOfGlobalRoles(usr, [
            Role.DeviceManager,
            Role.DeviceViewer,
            Role.OrganizationManager,
          ]) ||
          hasRoleInAnyUsersOrg(
            usr,
            [Role.DeviceManager, Role.DeviceViewer, Role.OrganizationManager],
            usr.orgs
          );

        if (!hasPerm) {
          // check if the user is global
          if (usr.globalRolesModeEnabled) {
            window.location.replace(`/global?id=${usr.id}`);
            return;
          }
          auth.signOut();
          localStorage.clear();
          window.location.replace('/login?error=noPerm');
          return;
        }

        dispatch({
          type: 'user-changed',
          data: usr,
        });
      }
    }, [usr, auth, dispatch]);

    return state.user ? (
      <AuthContext.Provider
        value={{
          authUser: state.user || initialUser,
          selectOrgs,
        }}
      >
        <Header />
        <div className={classes.root}>
          <Visible when={!resultsVisible}>
            <SelectOrgDialog />
          </Visible>
          <MediaDown query="sm">
            <Visible when={!resultsVisible}>
              <MainDrawer />
            </Visible>
          </MediaDown>
          <MediaUp query="md">
            <Visible when={!resultsVisible}>
              <MainDrawer />
            </Visible>
          </MediaUp>
          {!isLoading ? props.children : null}
        </div>
      </AuthContext.Provider>
    ) : null;
  }
);

export default AuthProvider;
